import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  declareAdminManualCollection,
  fetchAdminPaymentIntentDetail,
  holdAdminPayout,
  refundAdminPaymentIntent,
  releaseAdminPayout,
  replayAdminWebhook,
  retryAdminPayout,
  voidAdminManualCollection,
} from '../services/api/admin';
import type {
  AdminManualCollection,
  AdminManualCollectionSource,
  AdminPaymentIntent,
  AdminPaymentIntentDetail,
  AdminPayout,
  AdminWebhookEvent,
} from '../types/adminPaymentOps';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const STATE_CLASS_MAP: Record<string, string> = {
  AUTHORIZED: 'admin-pill-active',
  CAPTURED: 'admin-pill-success',
  SETTLED: 'admin-pill-success',
  PAID: 'admin-pill-success',
  SPLIT_SETTLED: 'admin-pill-success',
  PROCESSING: 'admin-pill-active',
  SUBMITTED: 'admin-pill-active',
  IN_BATCH: 'admin-pill-active',
  READY_FOR_BATCH: 'admin-pill-active',
  AUTH_PENDING: 'admin-pill-warning',
  CAPTURE_PENDING: 'admin-pill-warning',
  AUTH_REQUIRED_ACTION: 'admin-pill-warning',
  ON_HOLD: 'admin-pill-warning',
  DISPUTED: 'admin-pill-warning',
  AUTH_FAILED: 'admin-pill-danger',
  CAPTURE_FAILED: 'admin-pill-danger',
  CANCELLED: 'admin-pill-danger',
  CHARGEBACK: 'admin-pill-danger',
  FAILED: 'admin-pill-danger',
  TERMINAL_FAILURE: 'admin-pill-danger',
  MANUAL_REVIEW_REQUIRED: 'admin-pill-warning',
  MANUAL_COLLECTION_REQUIRED: 'admin-pill-warning',
  PARTIALLY_COLLECTED: 'admin-pill-warning',
  COLLECTED: 'admin-pill-success',
  VOIDED: 'admin-pill-danger',
};

function formatStateLabel(value: string | null | undefined): string {
  return value ? value.replaceAll('_', ' ') : 'Not set';
}

function formatOperationalState(
  kind: 'payment' | 'refund' | 'payout' | 'recovery',
  value: string | null | undefined,
): string {
  if (!value) return 'Not set';
  if (kind === 'refund' && value === 'NONE') return 'No refund';
  if (kind === 'recovery' && value === 'NONE') return 'No issue';
  if (kind === 'payout' && value === 'NOT_READY') return 'Not ready for driver payout';
  if (kind === 'payout' && value === 'SPLIT_SETTLED') return 'Split settled by Paystack';
  return formatStateLabel(value);
}

function formatMoney(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return 'Not set';
  return formatCurrency(value);
}

function shortId(value: string | null | undefined): string {
  if (!value) return 'Not set';
  return value.length > 12 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

function customerDisplayName(intent: AdminPaymentIntent): string {
  return intent.customerFullName?.trim() || intent.customerEmail?.trim() || 'Customer details unavailable';
}

function customerContact(intent: AdminPaymentIntent): string | null {
  if (intent.customerFullName?.trim() && intent.customerEmail?.trim()) return intent.customerEmail;
  return intent.customerMobileE164?.trim() || null;
}

function driverDisplayName(intent: AdminPaymentIntent): string {
  return intent.driverName?.trim() || `Driver profile ${shortId(intent.driverId)}`;
}

function detailValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'Not set';
  return String(value);
}

function operationalState(
  label: string,
  kind: 'payment' | 'refund' | 'payout' | 'recovery',
  value: string | null | undefined,
): JSX.Element {
  return (
    <span className="admin-payment-state-item">
      <small>{label}</small>
      <span className={`admin-pill ${value ? STATE_CLASS_MAP[value] || '' : ''}`}>
        {formatOperationalState(kind, value)}
      </span>
    </span>
  );
}

function renderJsonBlock(label: string, value: string | null | undefined): JSX.Element | null {
  if (!value) return null;

  let displayValue = value;
  try {
    displayValue = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    displayValue = value;
  }

  return (
    <details className="admin-payment-json-detail">
      <summary>{label}</summary>
      <pre className="admin-json-block">{displayValue}</pre>
    </details>
  );
}

function renderEmptyState(copy: string): JSX.Element {
  return <p className="admin-empty admin-payment-empty">{copy}</p>;
}

type PaymentOpsMutationKind = 'refund' | 'hold-payout' | 'release-payout' | 'retry-payout' | 'replay-webhook';

type ManualCollectionModalKind = 'declare' | 'void';

interface PaymentOpsMutationModalState {
  kind: PaymentOpsMutationKind;
  title: string;
  targetId: string;
  targetLabel: string;
  summary: string;
  idempotencyKey: string;
  maxAmount?: number | null;
  currency?: string | null;
}

interface ManualCollectionFormState {
  source: AdminManualCollectionSource;
  amount: string;
  agreedAmount: string;
  authorizeLoading: boolean;
  collectedAt: string;
  proofReference: string;
  receivedBy: string;
  reason: string;
  idempotencyKey: string;
}

interface ManualCollectionModalState {
  kind: ManualCollectionModalKind;
  targetId: string;
  title: string;
  targetLabel: string;
  collection?: AdminManualCollection;
}

function generateIdempotencyKey(kind: PaymentOpsMutationKind, targetId: string): string {
  const randomPart = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `admin-${kind}-${targetId}-${randomPart}`;
}

function generateManualCollectionIdempotencyKey(kind: ManualCollectionModalKind, targetId: string): string {
  const randomPart = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `manual-collection-${kind}-${targetId}-${randomPart}`;
}

function localDateTimeValue(date = new Date()): string {
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
}

function parsePositiveMoney(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100) / 100;
}

function toInstantFromLocalDateTime(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function hasRefundableBalance(detail: AdminPaymentIntentDetail): boolean {
  const intent = detail.paymentIntent;
  if (intent.paymentState !== 'CAPTURED' && intent.paymentState !== 'SETTLED') return false;
  if (intent.refundState === 'REFUNDED') return false;

  const capturedAmount = intent.capturedAmount ?? intent.grossAmount ?? intent.quotedAmount;
  if (capturedAmount === null || capturedAmount === undefined) return true;

  return (intent.refundedAmount ?? 0) < capturedAmount;
}

function refundableAmount(detail: AdminPaymentIntentDetail): number | null {
  const intent = detail.paymentIntent;
  const capturedAmount = intent.capturedAmount ?? intent.grossAmount ?? intent.quotedAmount;
  if (capturedAmount === null || capturedAmount === undefined) return null;
  return Math.max(capturedAmount - (intent.refundedAmount ?? 0), 0);
}

function canHoldPayout(payout: AdminPayout): boolean {
  return !['ON_HOLD', 'PAID', 'SPLIT_SETTLED', 'REVERSED', 'PROCESSING', 'SUBMITTED'].includes(payout.payoutState || '');
}

function canReleasePayout(payout: AdminPayout): boolean {
  return payout.payoutState === 'ON_HOLD';
}

function canRetryPayout(payout: AdminPayout): boolean {
  return payout.payoutState === 'FAILED';
}

function hasSplitAudit(intent: AdminPaymentIntentDetail['paymentIntent']): boolean {
  return Boolean(
    intent.splitStatus ||
    intent.paystackSubaccountCode ||
    intent.paystackSplitPercentage !== null && intent.paystackSplitPercentage !== undefined ||
    intent.payoutTargetType ||
    intent.payoutTargetId ||
    intent.splitResolvedAt ||
    intent.splitAppliedAt,
  );
}

function canReplayWebhook(event: AdminWebhookEvent): boolean {
  return event.signatureValid && event.processingState !== 'PROCESSED';
}

function canDeclareManualCollection(intent: AdminPaymentIntent): boolean {
  if (intent.paymentState === 'DISPUTED' || intent.paymentState === 'CHARGEBACK') return false;
  if (intent.refundState && intent.refundState !== 'NONE') return false;
  return true;
}

const AdminPaymentIntentDetailPage: React.FC = () => {
  const { publicId = '' } = useParams<{ publicId: string }>();
  const mountedRef = useRef(true);
  const [detail, setDetail] = useState<AdminPaymentIntentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<PaymentOpsMutationModalState | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [manualModal, setManualModal] = useState<ManualCollectionModalState | null>(null);
  const [manualForm, setManualForm] = useState<ManualCollectionFormState>(() => ({
    source: 'CASH',
    amount: '',
    agreedAmount: '',
    authorizeLoading: true,
    collectedAt: localDateTimeValue(),
    proofReference: '',
    receivedBy: '',
    reason: '',
    idempotencyKey: '',
  }));
  const [manualActionError, setManualActionError] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchAdminPaymentIntentDetail(publicId);
      if (mountedRef.current) setDetail(result);
    } catch (loadError: any) {
      if (mountedRef.current) setError(loadError.message || 'Could not load payment intent details.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    mountedRef.current = true;

    if (publicId) {
      void loadDetail();
    } else {
      setError('Payment intent ID is required.');
      setLoading(false);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [loadDetail, publicId]);

  const openMutationModal = (nextModal: Omit<PaymentOpsMutationModalState, 'idempotencyKey'>) => {
    setModal({
      ...nextModal,
      idempotencyKey: generateIdempotencyKey(nextModal.kind, nextModal.targetId),
    });
    setActionReason('');
    setRefundAmount('');
    setActionError('');
  };

  const closeMutationModal = () => {
    if (actionSubmitting) return;
    setModal(null);
    setActionReason('');
    setRefundAmount('');
    setActionError('');
  };

  const openDeclareManualCollectionModal = () => {
    if (!detail) return;
    const intent = detail.paymentIntent;
    const defaultAgreedAmount = intent.agreedAmount ?? intent.grossAmount ?? intent.quotedAmount ?? null;
    setManualModal({
      kind: 'declare',
      targetId: intent.publicId,
      title: 'Declare offline collection',
      targetLabel: `Payment intent ${shortId(intent.publicId)}`,
    });
    setManualForm({
      source: 'CASH',
      amount: '',
      agreedAmount: defaultAgreedAmount === null ? '' : String(defaultAgreedAmount),
      authorizeLoading: true,
      collectedAt: localDateTimeValue(),
      proofReference: '',
      receivedBy: '',
      reason: '',
      idempotencyKey: generateManualCollectionIdempotencyKey('declare', intent.publicId),
    });
    setManualActionError('');
  };

  const openVoidManualCollectionModal = (collection: AdminManualCollection) => {
    setManualModal({
      kind: 'void',
      targetId: collection.publicId,
      title: 'Void manual collection',
      targetLabel: `Collection ${shortId(collection.publicId)}`,
      collection,
    });
    setManualForm({
      source: 'CASH',
      amount: '',
      agreedAmount: '',
      authorizeLoading: false,
      collectedAt: '',
      proofReference: '',
      receivedBy: '',
      reason: '',
      idempotencyKey: generateManualCollectionIdempotencyKey('void', collection.publicId),
    });
    setManualActionError('');
  };

  const closeManualCollectionModal = () => {
    if (manualSubmitting) return;
    setManualModal(null);
    setManualActionError('');
  };

  const updateManualForm = (key: keyof ManualCollectionFormState, value: string | boolean) => {
    setManualForm((current) => ({ ...current, [key]: value }));
  };

  const parsedRefundAmount = useMemo(() => {
    if (!refundAmount.trim()) return undefined;
    const parsed = Number(refundAmount);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }, [refundAmount]);

  const parsedManualAmount = useMemo(() => parsePositiveMoney(manualForm.amount), [manualForm.amount]);
  const parsedManualAgreedAmount = useMemo(() => parsePositiveMoney(manualForm.agreedAmount), [manualForm.agreedAmount]);
  const isManualDeposit = Boolean(
    parsedManualAmount !== null
    && parsedManualAgreedAmount !== null
    && parsedManualAmount < parsedManualAgreedAmount,
  );

  const submitMutation = async () => {
    if (!modal) return;
    const reason = actionReason.trim();
    if (!reason) {
      setActionError('A typed reason is required before this action can be submitted.');
      return;
    }

    if (modal.kind === 'refund') {
      if (parsedRefundAmount !== undefined && (!Number.isFinite(parsedRefundAmount) || parsedRefundAmount <= 0)) {
        setActionError('Refund amount must be a positive number, or leave it blank for the default refund.');
        return;
      }
      if (
        parsedRefundAmount !== undefined
        && modal.maxAmount !== null
        && modal.maxAmount !== undefined
        && parsedRefundAmount > modal.maxAmount
      ) {
        setActionError(`Refund amount cannot exceed ${formatMoney(modal.maxAmount, modal.currency)}.`);
        return;
      }
    }

    setActionSubmitting(true);
    setActionError('');
    try {
      const payload = {
        reason,
        idempotencyKey: modal.idempotencyKey,
        ...(modal.kind === 'refund' && parsedRefundAmount !== undefined ? { amount: parsedRefundAmount } : {}),
      };

      if (modal.kind === 'refund') {
        await refundAdminPaymentIntent(modal.targetId, payload);
      } else if (modal.kind === 'hold-payout') {
        await holdAdminPayout(modal.targetId, payload);
      } else if (modal.kind === 'release-payout') {
        await releaseAdminPayout(modal.targetId, payload);
      } else if (modal.kind === 'retry-payout') {
        await retryAdminPayout(modal.targetId, payload);
      } else if (modal.kind === 'replay-webhook') {
        await replayAdminWebhook(modal.targetId, payload);
      }

      setModal(null);
      setActionReason('');
      setRefundAmount('');
      await loadDetail();
    } catch (submitError: any) {
      setActionError(submitError.message || 'Could not complete this admin action.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const submitManualCollection = async () => {
    if (!manualModal) return;
    const reason = manualForm.reason.trim();
    if (!reason) {
      setManualActionError('A typed reason is required before this action can be submitted.');
      return;
    }

    setManualSubmitting(true);
    setManualActionError('');
    try {
      if (manualModal.kind === 'declare') {
        if (parsedManualAmount === null) {
          setManualActionError('Enter a collection amount greater than zero.');
          return;
        }
        if (manualForm.agreedAmount.trim() && parsedManualAgreedAmount === null) {
          setManualActionError('Agreed amount must be greater than zero, or leave it blank to keep the current fare.');
          return;
        }

        await declareAdminManualCollection(manualModal.targetId, {
          source: manualForm.source,
          amount: parsedManualAmount,
          ...(parsedManualAgreedAmount !== null ? { agreedAmount: parsedManualAgreedAmount } : {}),
          authorizeLoading: manualForm.authorizeLoading,
          collectedAt: toInstantFromLocalDateTime(manualForm.collectedAt),
          proofReference: manualForm.proofReference,
          receivedBy: manualForm.receivedBy,
          reason,
          idempotencyKey: manualForm.idempotencyKey,
        });
      } else {
        await voidAdminManualCollection(manualModal.targetId, {
          reason,
          idempotencyKey: manualForm.idempotencyKey,
        });
      }

      setManualModal(null);
      await loadDetail();
    } catch (submitError: any) {
      setManualActionError(submitError.message || 'Could not complete this manual collection action.');
    } finally {
      setManualSubmitting(false);
    }
  };

  return (
    <AdminShell
      badge="Payment Operations"
      copy="Inspect the complete payment, payout, provider, webhook, ledger, and admin audit trail."
      title="Payment intent detail"
    >
      <div className="admin-inline-actions" style={{ marginBottom: '18px' }}>
        <Link className="register-secondary-button" to="/admin/payment-ops">
          Back to payment intents
        </Link>
      </div>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Operations picture</h2>
            <p className="admin-panel-copy">{publicId}</p>
          </div>
        </div>

        {error ? <div className="register-inline-error">{error}</div> : null}
        {loading ? <div className="register-loader" /> : null}

        {!loading && detail ? (
          <>
            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Payment intent summary</h3>
                <div className="admin-payment-heading-actions">
                  <span>{shortId(detail.paymentIntent.publicId)}</span>
                  {canDeclareManualCollection(detail.paymentIntent) ? (
                    <button
                      className="admin-payment-action-button"
                      onClick={openDeclareManualCollectionModal}
                      type="button"
                    >
                      Declare offline collection
                    </button>
                  ) : null}
                  {hasRefundableBalance(detail) ? (
                    <button
                      className="admin-payment-action-button admin-payment-action-danger"
                      onClick={() =>
                        openMutationModal({
                          kind: 'refund',
                          title: 'Refund payment intent',
                          targetId: detail.paymentIntent.publicId,
                          targetLabel: `Payment intent ${shortId(detail.paymentIntent.publicId)}`,
                          summary:
                            'Refunds money on a captured or settled payment intent. Leave amount blank only when the default refund is intended.',
                          maxAmount: refundableAmount(detail),
                          currency: detail.paymentIntent.currency,
                        })
                      }
                      type="button"
                    >
                      Refund
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="admin-payment-summary-grid">
                <div className="admin-detail-card">
                  <span>Primary booking</span>
                  <strong>{detailValue(detail.paymentIntent.primaryBookingId)}</strong>
                  <small>Payment intent {shortId(detail.paymentIntent.publicId)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Customer and driver</span>
                  <strong>{customerDisplayName(detail.paymentIntent)}</strong>
                  {customerContact(detail.paymentIntent) ? <small>{customerContact(detail.paymentIntent)}</small> : null}
                  <small>{driverDisplayName(detail.paymentIntent)}</small>
                  <small>Customer account {shortId(detail.paymentIntent.userId)}</small>
                  <small>Payment profile {shortId(detail.paymentIntent.paymentCustomerPublicId)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Provider</span>
                  <strong>{detailValue(detail.paymentIntent.provider)}</strong>
                  <small>Intent {detailValue(detail.paymentIntent.providerIntentId)}</small>
                  <small>Method {shortId(detail.paymentIntent.paymentMethodPublicId)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Operational status</span>
                  <div className="admin-payment-state-stack">
                    {operationalState('Payment', 'payment', detail.paymentIntent.paymentState)}
                    {operationalState('Refund', 'refund', detail.paymentIntent.refundState)}
                    {operationalState('Payout', 'payout', detail.paymentIntent.payoutState)}
                    {operationalState('Recovery', 'recovery', detail.paymentIntent.failureState)}
                  </div>
                </div>
                <div className="admin-detail-card">
                  <span>Money</span>
                  <strong>{formatMoney(detail.paymentIntent.grossAmount ?? detail.paymentIntent.quotedAmount, detail.paymentIntent.currency)}</strong>
                  <small>Quoted: {formatMoney(detail.paymentIntent.quotedAmount, detail.paymentIntent.currency)}</small>
                  <small>Authorized: {formatMoney(detail.paymentIntent.authorizedAmount, detail.paymentIntent.currency)}</small>
                  <small>Captured: {formatMoney(detail.paymentIntent.capturedAmount, detail.paymentIntent.currency)}</small>
                  <small>Refunded: {formatMoney(detail.paymentIntent.refundedAmount, detail.paymentIntent.currency)}</small>
                  <small>Driver net: {formatMoney(detail.paymentIntent.driverNetAmount, detail.paymentIntent.currency)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Offline collection</span>
                  <strong>{formatMoney(detail.paymentIntent.manuallyCollectedAmount, detail.paymentIntent.currency)}</strong>
                  <small>Source: {formatStateLabel(detail.paymentIntent.collectionSource)}</small>
                  <small>Status: {formatStateLabel(detail.paymentIntent.manualCollectionStatus)}</small>
                  <small>Agreed: {formatMoney(detail.paymentIntent.agreedAmount, detail.paymentIntent.currency)}</small>
                  <small>
                    Outstanding:{' '}
                    <b className={(detail.paymentIntent.outstandingAmount ?? 0) > 0 ? 'admin-payment-danger-text' : ''}>
                      {formatMoney(detail.paymentIntent.outstandingAmount, detail.paymentIntent.currency)}
                    </b>
                  </small>
                  <small>Loading authorized: {formatDateTime(detail.paymentIntent.manualCollectionAuthorizedAt)}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Fees and dates</span>
                  <strong>Platform fee {formatMoney(detail.paymentIntent.platformFeeAmount, detail.paymentIntent.currency)}</strong>
                  <small>Gateway fee: {formatMoney(detail.paymentIntent.gatewayFeeAmount, detail.paymentIntent.currency)}</small>
                  <small>Captured at: {formatDateTime(detail.paymentIntent.capturedAt)}</small>
                  <small>Auth expires: {formatDateTime(detail.paymentIntent.authExpiresAt)}</small>
                  <small>Customer action: {detail.paymentIntent.requiresCustomerAction ? 'Required' : 'Not required'}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Paystack split</span>
                  {hasSplitAudit(detail.paymentIntent) ? (
                    <>
                      <strong>{detailValue(detail.paymentIntent.splitStatus)}</strong>
                      <small>Paystack manages settlement for split-applied bookings.</small>
                      <small>Subaccount: {detailValue(detail.paymentIntent.paystackSubaccountCode)}</small>
                      <small>Split percentage: {detail.paymentIntent.paystackSplitPercentage ?? 'Not set'}</small>
                      <small>Payout target type: {detailValue(detail.paymentIntent.payoutTargetType)}</small>
                      <small>Payout target ID: {detailValue(detail.paymentIntent.payoutTargetId)}</small>
                      <small>Split resolved at: {formatDateTime(detail.paymentIntent.splitResolvedAt)}</small>
                      <small>Split applied at: {formatDateTime(detail.paymentIntent.splitAppliedAt)}</small>
                    </>
                  ) : (
                    <strong>Split not applied yet.</strong>
                  )}
                </div>
              </div>
            </div>

            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Manual collections</h3>
                <div className="admin-payment-heading-actions">
                  <span>{detail.manualCollections.length} records</span>
                  {canDeclareManualCollection(detail.paymentIntent) ? (
                    <button
                      className="admin-payment-action-button"
                      onClick={openDeclareManualCollectionModal}
                      type="button"
                    >
                      Add collection
                    </button>
                  ) : null}
                </div>
              </div>
              {detail.paymentIntent.outstandingAmount && detail.paymentIntent.outstandingAmount > 0 ? (
                <p className="admin-payment-note admin-payment-warning-note">
                  Outstanding balance: {formatMoney(detail.paymentIntent.outstandingAmount, detail.paymentIntent.currency)}. The trip can continue through unloading, but payout stays held until collection is complete.
                </p>
              ) : null}
              {detail.manualCollections.length ? (
                <div className="admin-payment-record-list">
                  {detail.manualCollections.map((collection) => (
                    <div className="admin-payment-record admin-payment-record-manual" key={collection.publicId}>
                      <div>
                        <span className="admin-payment-record-label">Collection</span>
                        <strong>{shortId(collection.publicId)}</strong>
                        <small>Payment intent {shortId(collection.paymentIntentPublicId)}</small>
                        <small>Driver {shortId(collection.driverId)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Source and status</span>
                        <strong>{formatStateLabel(collection.source)}</strong>
                        <span className={`admin-pill ${collection.status ? STATE_CLASS_MAP[collection.status] || '' : ''}`}>
                          {formatStateLabel(collection.status)}
                        </span>
                        <small>Loading: {collection.loadingAuthorized ? 'Authorized' : 'Not authorized'}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Money</span>
                        <strong>{formatMoney(collection.amount, collection.currency)}</strong>
                        <small>Agreed snapshot: {formatMoney(collection.agreedAmountSnapshot, collection.currency)}</small>
                        <small>Outstanding after: {formatMoney(collection.outstandingAfter, collection.currency)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Receipt</span>
                        <strong>{detailValue(collection.proofReference)}</strong>
                        <small>Received by: {detailValue(collection.receivedBy)}</small>
                        <small>Declared by: {detailValue(collection.declaredBy)}</small>
                        <small>Collected: {formatDateTime(collection.collectedAt)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Reason</span>
                        <strong>{detailValue(collection.reason)}</strong>
                        <small>Voided: {formatDateTime(collection.voidedAt)}</small>
                        <small>{collection.voidReason ? `Void reason: ${collection.voidReason}` : 'No void recorded'}</small>
                        {collection.status !== 'VOIDED' ? (
                          <div className="admin-payment-row-actions">
                            <button
                              className="admin-payment-action-button admin-payment-action-danger"
                              onClick={() => openVoidManualCollectionModal(collection)}
                              type="button"
                            >
                              Void
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderEmptyState('No manual collections are linked to this payment intent yet.')
              )}
            </div>

            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Related payouts</h3>
                <span>{detail.payouts.length} total</span>
              </div>
              <p className="admin-payment-note">
                Split-settled payments are provider-managed and should not be submitted through Paystack Transfers.
              </p>
              {detail.payouts.length ? (
                <div className="admin-payment-record-list">
                  {detail.payouts.map((payout) => (
                    <div className="admin-payment-record" key={payout.publicId}>
                      <div>
                        <span className="admin-payment-record-label">Payout</span>
                        <strong>{shortId(payout.publicId)}</strong>
                        <small>Driver {shortId(payout.driverId)}</small>
                        <small>Payment intent {shortId(payout.paymentIntentPublicId)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">State</span>
                        <span className={`admin-pill ${payout.payoutState ? STATE_CLASS_MAP[payout.payoutState] || '' : ''}`}>
                          {formatOperationalState('payout', payout.payoutState)}
                        </span>
                        <small>Retry count: {detailValue(payout.retryCount)}</small>
                        <small>Next retry: {formatDateTime(payout.nextRetryAt)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Provider</span>
                        <strong>{detailValue(payout.provider)}</strong>
                        <small>Payout ID {detailValue(payout.providerPayoutId)}</small>
                        <small>Transfer {detailValue(payout.providerTransferReference)}</small>
                        <small>Recipient {detailValue(payout.providerRecipientId)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Money and dates</span>
                        <strong>{formatMoney(payout.amount, payout.currency)}</strong>
                        <small>Available: {formatDateTime(payout.availableOn)}</small>
                        <small>Initiated: {formatDateTime(payout.initiatedAt)}</small>
                        <small>Settled: {formatDateTime(payout.settledAt)}</small>
                        <small>Failed: {formatDateTime(payout.failedAt)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Failure</span>
                        <strong>{detailValue(payout.failureCode)}</strong>
                        <small>{detailValue(payout.failureMessage)}</small>
                        <small>Bank ref {detailValue(payout.bankAccountRef)}</small>
                        <div className="admin-payment-row-actions">
                          {canHoldPayout(payout) ? (
                            <button
                              className="admin-payment-action-button"
                              onClick={() =>
                                openMutationModal({
                                  kind: 'hold-payout',
                                  title: 'Hold payout',
                                  targetId: payout.publicId,
                                  targetLabel: `Payout ${shortId(payout.publicId)}`,
                                  summary: 'Places this payout on hold and prevents it from moving forward until reviewed.',
                                })
                              }
                              type="button"
                            >
                              Hold
                            </button>
                          ) : null}
                          {canReleasePayout(payout) ? (
                            <button
                              className="admin-payment-action-button"
                              onClick={() =>
                                openMutationModal({
                                  kind: 'release-payout',
                                  title: 'Release payout',
                                  targetId: payout.publicId,
                                  targetLabel: `Payout ${shortId(payout.publicId)}`,
                                  summary: 'Releases an ON HOLD payout back into the payout flow.',
                                })
                              }
                              type="button"
                            >
                              Release
                            </button>
                          ) : null}
                          {canRetryPayout(payout) ? (
                            <button
                              className="admin-payment-action-button"
                              onClick={() =>
                                openMutationModal({
                                  kind: 'retry-payout',
                                  title: 'Retry payout',
                                  targetId: payout.publicId,
                                  targetLabel: `Payout ${shortId(payout.publicId)}`,
                                  summary: 'Retries a failed payout using the payment service retry flow.',
                                })
                              }
                              type="button"
                            >
                              Retry
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderEmptyState('No payouts are linked to this payment intent yet.')
              )}
            </div>

            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Provider event timeline</h3>
                <span>{detail.providerEvents.length} events</span>
              </div>
              {detail.providerEvents.length ? (
                <div className="admin-payment-timeline">
                  {detail.providerEvents.map((event) => (
                    <article className="admin-payment-timeline-item" key={event.publicId}>
                      <div className="admin-payment-timeline-marker" />
                      <div className="admin-payment-timeline-body">
                        <div className="admin-payment-timeline-header">
                          <div>
                            <strong>{detailValue(event.eventType)}</strong>
                            <small>{formatDateTime(event.recordedAt)}</small>
                          </div>
                          <span className={`admin-pill ${event.success ? 'admin-pill-success' : 'admin-pill-danger'}`}>
                            {event.success ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        <div className="admin-payment-meta-grid">
                          <small>Direction: {detailValue(event.eventDirection)}</small>
                          <small>Provider: {detailValue(event.provider)}</small>
                          <small>HTTP: {detailValue(event.httpStatus)}</small>
                          <small>Reference: {detailValue(event.providerReference)}</small>
                          <small>Payment intent: {shortId(event.paymentIntentPublicId)}</small>
                          <small>Event ID: {shortId(event.publicId)}</small>
                        </div>
                        {renderJsonBlock('Request JSON', event.requestJson)}
                        {renderJsonBlock('Response JSON', event.responseJson)}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                renderEmptyState('No provider events were found for this payment intent.')
              )}
            </div>

            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Webhook event timeline</h3>
                <span>{detail.webhookEvents.length} events</span>
              </div>
              {detail.webhookEvents.length ? (
                <div className="admin-payment-timeline">
                  {detail.webhookEvents.map((event) => (
                    <article className="admin-payment-timeline-item" key={event.publicId}>
                      <div className="admin-payment-timeline-marker" />
                      <div className="admin-payment-timeline-body">
                        <div className="admin-payment-timeline-header">
                          <div>
                            <strong>{detailValue(event.eventType)}</strong>
                            <small>Received {formatDateTime(event.receivedAt)}</small>
                          </div>
                          <div className="admin-payment-heading-actions">
                            <span className={`admin-pill ${STATE_CLASS_MAP[event.processingState || ''] || ''}`}>
                              {formatStateLabel(event.processingState)}
                            </span>
                            {canReplayWebhook(event) ? (
                              <button
                                className="admin-payment-action-button"
                                onClick={() =>
                                  openMutationModal({
                                    kind: 'replay-webhook',
                                    title: 'Replay webhook',
                                    targetId: event.publicId,
                                    targetLabel: `Webhook ${shortId(event.publicId)}`,
                                    summary: 'Replays a valid webhook that has not already been processed.',
                                  })
                                }
                                type="button"
                              >
                                Replay
                              </button>
                            ) : null}
                          </div>
                        </div>
                        <div className="admin-payment-meta-grid">
                          <small>Provider: {detailValue(event.provider)}</small>
                          <small>Provider event: {detailValue(event.providerEventId)}</small>
                          <small>Signature: {event.signatureValid ? 'Valid' : 'Invalid'}</small>
                          <small>Processed: {formatDateTime(event.processedAt)}</small>
                          <small>Payload hash: {detailValue(event.payloadHash)}</small>
                          <small>Webhook ID: {shortId(event.publicId)}</small>
                        </div>
                        {event.failureMessage ? <p className="admin-payment-note">{event.failureMessage}</p> : null}
                        {renderJsonBlock('Payload JSON', event.payloadJson)}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                renderEmptyState('No webhook events were found for this payment intent.')
              )}
            </div>

            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Ledger entries</h3>
                <span>{detail.ledgerEntries.length} entries</span>
              </div>
              {detail.ledgerEntries.length ? (
                <div className="admin-payment-record-list">
                  {detail.ledgerEntries.map((entry) => (
                    <div className="admin-payment-record admin-payment-record-ledger" key={entry.publicId}>
                      <div>
                        <span className="admin-payment-record-label">Entry</span>
                        <strong>{detailValue(entry.entryType)}</strong>
                        <small>{formatDateTime(entry.eventAt)}</small>
                        <small>Created {formatDateTime(entry.createdAt)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Side and account</span>
                        <strong>{detailValue(entry.entrySide)}</strong>
                        <small>Account {detailValue(entry.accountCode)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Amount</span>
                        <strong>{formatMoney(entry.amount, entry.currency)}</strong>
                        <small>{detailValue(entry.description)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Reference</span>
                        <strong>{detailValue(entry.referenceType)}</strong>
                        <small>{detailValue(entry.referenceId)}</small>
                        <small>Payout {shortId(entry.payoutPublicId)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderEmptyState('No ledger entries were found for this payment intent.')
              )}
            </div>

            <div className="admin-payment-detail-section">
              <div className="admin-payment-section-heading">
                <h3>Admin action audit trail</h3>
                <span>{detail.adminActions.length} actions</span>
              </div>
              {detail.adminActions.length ? (
                <div className="admin-payment-timeline">
                  {detail.adminActions.map((action) => (
                    <article className="admin-payment-timeline-item" key={action.publicId}>
                      <div className="admin-payment-timeline-marker" />
                      <div className="admin-payment-timeline-body">
                        <div className="admin-payment-timeline-header">
                          <div>
                            <strong>{detailValue(action.actionType)}</strong>
                            <small>{formatDateTime(action.createdAt)}</small>
                          </div>
                          <span className={`admin-pill ${STATE_CLASS_MAP[action.resultState || ''] || ''}`}>
                            {detailValue(action.resultState)}
                          </span>
                        </div>
                        <div className="admin-payment-meta-grid">
                          <small>Requested by: {detailValue(action.requestedBy)}</small>
                          <small>Target: {detailValue(action.targetType)} {shortId(action.targetPublicId)}</small>
                          <small>Payment intent: {shortId(action.paymentIntentPublicId)}</small>
                          <small>Payout: {shortId(action.payoutPublicId)}</small>
                          <small>Webhook: {shortId(action.webhookEventPublicId)}</small>
                          <small>Idempotency: {detailValue(action.idempotencyKey)}</small>
                        </div>
                        <p className="admin-payment-note">Reason: {detailValue(action.reason)}</p>
                        <p className="admin-payment-note">Result: {detailValue(action.resultMessage)}</p>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                renderEmptyState('No admin actions have been recorded for this payment intent.')
              )}
            </div>
          </>
        ) : null}
      </section>

      {manualModal ? (
        <div className="admin-modal-backdrop" role="presentation">
          <div aria-modal="true" className="admin-modal" role="dialog">
            <div className="admin-modal-header">
              <div>
                <h2>{manualModal.title}</h2>
                <p>{manualModal.targetLabel}</p>
              </div>
              <button
                aria-label="Close manual collection modal"
                className="admin-modal-close"
                disabled={manualSubmitting}
                onClick={closeManualCollectionModal}
                type="button"
              >
                x
              </button>
            </div>

            {manualModal.kind === 'declare' ? (
              <>
                <div className="admin-modal-grid">
                  <label className="admin-modal-field">
                    <span>Source</span>
                    <select
                      className="register-input"
                      disabled={manualSubmitting}
                      onChange={(event) => updateManualForm('source', event.target.value as AdminManualCollectionSource)}
                      value={manualForm.source}
                    >
                      <option value="CASH">Cash</option>
                      <option value="DIRECT_EFT">Direct EFT</option>
                    </select>
                  </label>
                  <label className="admin-modal-field">
                    <span>Amount collected</span>
                    <input
                      className="register-input"
                      disabled={manualSubmitting}
                      inputMode="decimal"
                      min="0.01"
                      onChange={(event) => updateManualForm('amount', event.target.value)}
                      placeholder="3000.00"
                      step="0.01"
                      type="number"
                      value={manualForm.amount}
                    />
                  </label>
                  <label className="admin-modal-field">
                    <span>Agreed amount</span>
                    <input
                      className="register-input"
                      disabled={manualSubmitting}
                      inputMode="decimal"
                      min="0.01"
                      onChange={(event) => updateManualForm('agreedAmount', event.target.value)}
                      placeholder="Leave blank to keep current fare"
                      step="0.01"
                      type="number"
                      value={manualForm.agreedAmount}
                    />
                  </label>
                  <label className="admin-modal-field">
                    <span>Collected at</span>
                    <input
                      className="register-input"
                      disabled={manualSubmitting}
                      onChange={(event) => updateManualForm('collectedAt', event.target.value)}
                      type="datetime-local"
                      value={manualForm.collectedAt}
                    />
                  </label>
                </div>

                <label className="admin-modal-toggle">
                  <input
                    checked={manualForm.authorizeLoading}
                    disabled={manualSubmitting}
                    onChange={(event) => updateManualForm('authorizeLoading', event.target.checked)}
                    type="checkbox"
                  />
                  <span>Authorize driver loading from this declaration</span>
                </label>

                {isManualDeposit ? (
                  <p className="admin-payment-note admin-payment-warning-note">
                    This is a deposit. Loading can be authorized, and the balance will remain visible without blocking unloading.
                  </p>
                ) : null}

                <label className="admin-modal-field">
                  <span>Proof or bank reference</span>
                  <input
                    className="register-input"
                    disabled={manualSubmitting}
                    maxLength={160}
                    onChange={(event) => updateManualForm('proofReference', event.target.value)}
                    placeholder="Receipt number, bank statement reference, or cash note"
                    value={manualForm.proofReference}
                  />
                </label>

                <label className="admin-modal-field">
                  <span>Received by</span>
                  <input
                    className="register-input"
                    disabled={manualSubmitting}
                    maxLength={160}
                    onChange={(event) => updateManualForm('receivedBy', event.target.value)}
                    placeholder="Driver, cashier, bank account, or staff member"
                    value={manualForm.receivedBy}
                  />
                </label>
              </>
            ) : (
              <p className="admin-payment-note">
                This reverses collection {shortId(manualModal.collection?.publicId)} and recalculates the collected and outstanding totals.
              </p>
            )}

            <label className="admin-modal-field">
              <span>Reason</span>
              <textarea
                className="register-input"
                disabled={manualSubmitting}
                maxLength={255}
                onChange={(event) => updateManualForm('reason', event.target.value)}
                placeholder="Type the operational reason for this manual collection action"
                rows={4}
                value={manualForm.reason}
              />
            </label>

            <div className="admin-modal-key">
              <span>Idempotency key</span>
              <code>{manualForm.idempotencyKey}</code>
            </div>

            {manualActionError ? <div className="register-inline-error">{manualActionError}</div> : null}

            <div className="admin-modal-actions">
              <button
                className="register-secondary-button"
                disabled={manualSubmitting}
                onClick={closeManualCollectionModal}
                type="button"
              >
                Cancel
              </button>
              <button
                className="register-secondary-button"
                disabled={
                  manualSubmitting
                  || !manualForm.reason.trim()
                  || (manualModal.kind === 'declare' && parsedManualAmount === null)
                }
                onClick={() => void submitManualCollection()}
                type="button"
              >
                {manualSubmitting ? 'Submitting...' : manualModal.kind === 'declare' ? 'Declare collection' : 'Void collection'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modal ? (
        <div className="admin-modal-backdrop" role="presentation">
          <div aria-modal="true" className="admin-modal" role="dialog">
            <div className="admin-modal-header">
              <div>
                <h2>{modal.title}</h2>
                <p>{modal.targetLabel}</p>
              </div>
              <button
                aria-label="Close action modal"
                className="admin-modal-close"
                disabled={actionSubmitting}
                onClick={closeMutationModal}
                type="button"
              >
                x
              </button>
            </div>

            <p className="admin-payment-note">{modal.summary}</p>

            <label className="admin-modal-field">
              <span>Reason</span>
              <textarea
                className="register-input"
                disabled={actionSubmitting}
                onChange={(event) => setActionReason(event.target.value)}
                placeholder="Type the operational reason for this action"
                rows={4}
                value={actionReason}
              />
            </label>

            {modal.kind === 'refund' ? (
              <label className="admin-modal-field">
                <span>Refund amount optional</span>
                <input
                  className="register-input"
                  disabled={actionSubmitting}
                  inputMode="decimal"
                  max={modal.maxAmount ?? undefined}
                  min="0"
                  onChange={(event) => setRefundAmount(event.target.value)}
                  placeholder={
                    modal.maxAmount
                      ? `Leave blank or enter up to ${formatMoney(modal.maxAmount, modal.currency)}`
                      : 'Leave blank for default refund'
                  }
                  step="0.01"
                  type="number"
                  value={refundAmount}
                />
              </label>
            ) : null}

            <div className="admin-modal-key">
              <span>Idempotency key</span>
              <code>{modal.idempotencyKey}</code>
            </div>

            {actionError ? <div className="register-inline-error">{actionError}</div> : null}

            <div className="admin-modal-actions">
              <button
                className="register-secondary-button"
                disabled={actionSubmitting}
                onClick={closeMutationModal}
                type="button"
              >
                Cancel
              </button>
              <button
                className="register-secondary-button"
                disabled={actionSubmitting || !actionReason.trim()}
                onClick={() => void submitMutation()}
                type="button"
              >
                {actionSubmitting ? 'Submitting...' : 'Submit action'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminPaymentIntentDetailPage;
